import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { Controller, Control } from "react-hook-form";
import { SubmissionReviewState } from "hooks/submission/reviewStates";
import { SignatureType } from "generated-graphql/graphql";

interface ReviewFormValues {
  reviewFinished: boolean;
}

interface SubmissionDisplayReviewOrSignDocumentProps {
  isReviewerASigner: boolean;
  reviewState: SubmissionReviewState;
  reviewFinished: boolean;
  submissionDocumentReviewId: string | null;
  signingCompleted: boolean;
  signatureType?: SignatureType | null;
  isMutating: boolean;
  control: Control<ReviewFormValues>;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export function SubmissionDisplayReviewOrSignDocument({
  isReviewerASigner,
  reviewState,
  reviewFinished,
  submissionDocumentReviewId,
  signingCompleted,
  signatureType,
  isMutating,
  control,
  onSubmit,
}: SubmissionDisplayReviewOrSignDocumentProps) {
  const isSignerWaitingForPrereqs = useMemo(
    () => reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS,
    [reviewState]
  );

  const isWetInkSigner =
    isReviewerASigner && signatureType === SignatureType.WetInk;

  const isSubmitDisabled = useMemo(() => {
    if (isSignerWaitingForPrereqs) {
      return true;
    }
    if (reviewState === SubmissionReviewState.SIGNER_CAN_SIGN) {
      // For digital signatures, require signing completed
      // For wet ink, signers just need to approve (signing happens offline)
      if (!isWetInkSigner && reviewFinished && !signingCompleted) {
        return true;
      }
    }
    return !submissionDocumentReviewId || isMutating;
  }, [
    isSignerWaitingForPrereqs,
    reviewFinished,
    signingCompleted,
    submissionDocumentReviewId,
    isMutating,
    reviewState,
    isWetInkSigner,
  ]);

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Stack spacing={2} sx={{ height: "100%" }}>
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <FormControl
            component="fieldset"
            disabled={isSignerWaitingForPrereqs}
            sx={{ mb: 3 }}
          >
            <FormLabel component="legend">
              {isReviewerASigner
                ? isWetInkSigner
                  ? "Review document (wet ink signature)"
                  : "Sign complete"
                : "Review submission"}
            </FormLabel>
            <Controller
              name="reviewFinished"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  value={field.value ? "true" : "false"}
                  onChange={(event) =>
                    field.onChange(event.target.value === "true")
                  }
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label={`✅ ${
                      isReviewerASigner
                        ? isWetInkSigner
                          ? "Ready for wet ink signature (will be mailed)"
                          : "Done signing, ready for submission"
                        : "Good to go, ready for signature"
                    }`}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="🔄 Needs edits, specified in comments"
                  />
                </RadioGroup>
              )}
            />
          </FormControl>

          {submissionDocumentReviewId && !isReviewerASigner && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {reviewFinished
                ? "We will approve this review and move it forward for signature."
                : "We will send this back for edits based on your comments."}
            </Typography>
          )}

          {!submissionDocumentReviewId && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review actions will be available once a document review is
              assigned.
            </Typography>
          )}

          <Box
            sx={{
              mt: "auto",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitDisabled}
            >
              {/* If the signer is reviewing for a wet ink signature the button should use the default */}
              {isReviewerASigner && !isWetInkSigner
                ? "Finish"
                : "Review complete"}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Grid>
  );
}
